import { format } from 'date-fns';

import { getEnvRequired } from '../../shared/utilities/env';
import { getAuthToken } from '../login/operations';

import {
    DiscussionIsResponseRequiredState,
    DiscussionItem,
    Message,
    MessagesGroupByDate,
} from './types';

export const generateSendingMessageId = (): string => {
    return String(new Date().getTime());
};

/**
 * Generates a unique identifier for attachment tracking during upload.
 *
 * Combines filename with timestamp and random suffix to ensure uniqueness
 * when multiple files are uploaded simultaneously.
 *
 * @param filename - The name of the file being uploaded
 * @returns A unique identifier string in format: `{filename}-{timestamp}-{randomSuffix}`
 *
 * @example
 * generateAttachmentId('document.pdf')
 * // Returns: 'document.pdf-1706025600000-a3x8k9m2p'
 */
export const generateAttachmentId = (filename: string): string => {
    const timestamp = new Date().getTime();
    const randomSuffix = Math.random().toString(36).substring(2, 11);
    return `${filename}-${timestamp}-${randomSuffix}`;
};

export const addAttachmentLinkToMessage = (message: Message): Message => ({
    ...message,
    attachments: message.attachments.map((attachment) => ({
        ...attachment,
        link: `${getEnvRequired('DISCUSSION_API_URL')}/discussions/messages/${
            message.id
        }/attachments/${attachment.id}?jwt=${getAuthToken()}`,
    })),
});

export const groupDiscussionItemByDate = (
    acc: MessagesGroupByDate,
    discussionItem: DiscussionItem,
): MessagesGroupByDate => {
    const date = format(discussionItem.sendDate, 'yyyy-MM-dd');
    acc[date] = [...(acc[date] || []), discussionItem];
    return acc;
};

export const getIsResponseRequiredState = (
    isLastMessageFromTraveller = false,
    isResponseRequired: boolean,
): DiscussionIsResponseRequiredState => {
    if (isLastMessageFromTraveller && isResponseRequired) {
        return 'TO_MARK_AS_NO_RESPONSE_REQUIRED';
    }
    if (isLastMessageFromTraveller && !isResponseRequired) {
        return 'MANUALLY_MARKED_AS_NO_RESPONSE_REQUIRED';
    }
    return 'AUTOMATICALLY_MARKED_AS_NO_RESPONSE_REQUIRED';
};
